import React, { useState, useEffect } from 'react';
import { Edit, Plus, X, Globe } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import { FEE_LABELS, feeLabel } from '../../lib/visaFees';

const CUSTOM = '__custom__';

const feesToRows = (fees = {}) =>
  Object.entries(fees)
    .filter(([k]) => k !== 'contact_required')
    .map(([key, value]) => ({
      key: FEE_LABELS[key] ? key : CUSTOM,
      custom: FEE_LABELS[key] ? '' : key,
      value: String(value)
    }));

const rowsToFees = (rows, onRequest) => {
  if (onRequest) return { contact_required: true };
  const fees = {};
  rows.forEach((r) => {
    const key = r.key === CUSTOM ? r.custom.trim() : r.key;
    const v = r.value.trim();
    if (!key || v === '') return;
    fees[key] = v !== '' && !isNaN(Number(v)) ? Number(v) : v;
  });
  return fees;
};

const FeeRow = ({ row, index, onChange, onRemove }) => (
  <div className="flex gap-2 items-center" data-testid={`fee-row-${index}`}>
    <Select value={row.key} onValueChange={(v) => onChange({ ...row, key: v })}>
      <SelectTrigger className="bg-white/5 border-white/10 w-48" data-testid={`fee-key-select-${index}`}><SelectValue placeholder="Type de frais" /></SelectTrigger>
      <SelectContent className="bg-[#1E293B] border-white/10 text-white">
        {Object.entries(FEE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
        <SelectItem value={CUSTOM}>Autre (libellé libre)</SelectItem>
      </SelectContent>
    </Select>
    {row.key === CUSTOM && (
      <Input value={row.custom} onChange={(e) => onChange({ ...row, custom: e.target.value })} placeholder="Libellé" className="bg-white/5 border-white/10" data-testid={`fee-custom-label-${index}`} />
    )}
    <Input value={row.value} onChange={(e) => onChange({ ...row, value: e.target.value })} placeholder="Montant $ ou texte" className="bg-white/5 border-white/10 w-40" data-testid={`fee-value-input-${index}`} />
    <button type="button" onClick={onRemove} className="text-gray-400 hover:text-red-400" data-testid={`fee-remove-${index}`}><X className="w-4 h-4" /></button>
  </div>
);

const ListEditor = ({ items, onChange, placeholder, testid }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    if (!draft.trim()) return;
    onChange([...items, draft.trim()]);
    setDraft('');
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder={placeholder} className="bg-white/5 border-white/10" data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" className="border-white/10" onClick={add} data-testid={`${testid}-add-btn`}><Plus className="w-4 h-4" /></Button>
      </div>
      {items.map((it, i) => (
        <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/5 text-sm" data-testid={`${testid}-item-${i}`}>
          <span>{it}</span>
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-400" data-testid={`${testid}-remove-${i}`}><X className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
};

const feeSummary = (fees = {}) => {
  if (fees.contact_required) return 'Tarif sur demande';
  return Object.entries(fees).map(([k, v]) => `${feeLabel(k)} : ${typeof v === 'number' ? `${v}$` : v}`).join(' · ');
};

const VisaTypeRow = ({ type, onEdit, testid }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-t border-white/5 first:border-t-0" data-testid={testid}>
    <div className="min-w-0">
      <p className="font-medium text-white">{type.name}</p>
      <p className="text-xs text-sky-300 mt-1">{feeSummary(type.fees)}</p>
    </div>
    <Button variant="outline" size="sm" className="border-white/10 text-gray-300 hover:text-white flex-shrink-0" onClick={onEdit} data-testid={`${testid}-edit-btn`}>
      <Edit className="w-4 h-4 mr-1" /> Modifier
    </Button>
  </div>
);

export default function VisaPricesPage() {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/admin/visa-destinations');
      setDestinations(r.data);
    } catch {
      toast.error('Erreur de chargement des visas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openEdit = (dest, type) => {
    setEditing({ destId: dest.id, typeId: type.id, country: dest.country });
    setForm({
      name: type.name || '', description: type.description || '', duration: type.duration || '',
      rows: feesToRows(type.fees), onRequest: !!type.fees?.contact_required,
      documents_required: type.documents_required || [], conditions: type.conditions || [],
      note: type.note || '', payment_terms: type.payment_terms || ''
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Le nom du visa est requis');
    setSaving(true);
    try {
      const { rows, onRequest, ...rest } = form;
      await api.put(`/admin/visa-destinations/${editing.destId}/visa-types/${editing.typeId}`, {
        ...rest, fees: rowsToFees(rows, onRequest), note: rest.note || null, payment_terms: rest.payment_terms || null
      });
      toast.success('Visa mis à jour');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.toString() || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gray-400" data-testid="visa-prices-loading">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="visa-prices-page">
      <div>
        <h2 className="text-2xl font-bold text-white">Prix & Visas</h2>
        <p className="text-gray-400 text-sm">Modifiez les tarifs et détails affichés sur les pages visa du site</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {destinations.map((dest) => (
          <Card key={dest.id} className="bg-[#0F172A] border-white/10" data-testid={`visa-dest-card-${dest.id}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-5 h-5 text-sky-400" />
                <h3 className="text-lg font-semibold text-white">{dest.country}</h3>
              </div>
              {dest.visa_types.map((t) => (
                <VisaTypeRow key={t.id} type={t} onEdit={() => openEdit(dest, t)} testid={`visa-type-${dest.id}-${t.id}`} />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white" data-testid="visa-type-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.country} — {form?.name}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Nom du visa *</Label>
                  <Input value={form.name} onChange={(e) => set('name', e.target.value)} className="bg-white/5 border-white/10" data-testid="visa-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Durée</Label>
                  <Input value={form.duration} onChange={(e) => set('duration', e.target.value)} className="bg-white/5 border-white/10" data-testid="visa-duration-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} className="bg-white/5 border-white/10" data-testid="visa-description-input" />
              </div>

              <div className="space-y-3 p-4 rounded-xl border border-sky-500/20 bg-sky-500/5">
                <div className="flex items-center justify-between">
                  <Label className="text-sky-300">Prix et frais</Label>
                  <div className="flex items-center gap-2">
                    <Switch checked={form.onRequest} onCheckedChange={(v) => set('onRequest', v)} data-testid="visa-on-request-switch" />
                    <span className="text-xs text-gray-400">Tarif sur demande</span>
                  </div>
                </div>
                {form.onRequest ? (
                  <p className="text-xs text-amber-400">Le site affichera « Nous contacter pour les tarifs ».</p>
                ) : (
                  <>
                    {form.rows.map((row, i) => (
                      <FeeRow key={i} row={row} index={i}
                        onChange={(r) => set('rows', form.rows.map((x, j) => (j === i ? r : x)))}
                        onRemove={() => set('rows', form.rows.filter((_, j) => j !== i))} />
                    ))}
                    <Button type="button" variant="outline" size="sm" className="border-white/10" onClick={() => set('rows', [...form.rows, { key: 'honoraires', custom: '', value: '' }])} data-testid="fee-add-btn">
                      <Plus className="w-4 h-4 mr-1" /> Ajouter une ligne de frais
                    </Button>
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label>Documents requis</Label>
                <ListEditor items={form.documents_required} onChange={(v) => set('documents_required', v)} placeholder="Ex : Passeport valide" testid="visa-doc" />
              </div>
              <div className="space-y-2">
                <Label>Conditions / avantages</Label>
                <ListEditor items={form.conditions} onChange={(v) => set('conditions', v)} placeholder="Ex : Accompagnement complet" testid="visa-condition" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Modalités de paiement</Label>
                  <Input value={form.payment_terms} onChange={(e) => set('payment_terms', e.target.value)} className="bg-white/5 border-white/10" data-testid="visa-payment-terms-input" />
                </div>
                <div className="space-y-2">
                  <Label>Note</Label>
                  <Input value={form.note} onChange={(e) => set('note', e.target.value)} className="bg-white/5 border-white/10" data-testid="visa-note-input" />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" className="border-white/10" onClick={() => setEditing(null)} data-testid="visa-cancel-btn">Annuler</Button>
                <Button type="submit" disabled={saving} className="bg-sky-500 hover:bg-sky-600" data-testid="visa-save-btn">
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
